import React, { useState, useEffect, useRef } from 'react';
import { Artist } from '../services/ClientArtistSearch';
import { logger } from '../utils/logger';
import { analytics } from '../services/analytics/AnalyticsService';

interface MockArtistAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (artist: Artist) => void;
  placeholder?: string;
  className?: string;
}

const debounce = (func: Function, wait: number) => {
  let timeout: NodeJS.Timeout;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

const MockArtistAutocomplete: React.FC<MockArtistAutocompleteProps> = ({
  value,
  onChange,
  onSelect,
  placeholder = "Search for an artist...",
  className = ""
}) => {
  const [suggestions, setSuggestions] = useState<Artist[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [dropdownPosition, setDropdownPosition] = useState<'below' | 'above'>('below');
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Calculate optimal dropdown position
  const calculateDropdownPosition = () => {
    if (!inputRef.current) return 'below';
    
    const inputRect = inputRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownMaxHeight = 240; // max-h-60 = 240px
    const bottomSpace = viewportHeight - inputRect.bottom;
    const topSpace = inputRect.top;
    
    // If there's not enough space below but enough space above, show above
    if (bottomSpace < dropdownMaxHeight + 20 && topSpace > dropdownMaxHeight + 20) {
      return 'above';
    }
    
    return 'below';
  };

  const debouncedSearch = useRef(
    debounce(async (query: string) => {
      // Don't search for queries less than 2 characters
      if (query.length < 2) {
        setSuggestions([]);
        setShowDropdown(false);
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Use the new Spotify-first API backend
        const apiUrl = (window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.startsWith('192.168.'))
          ? 'http://localhost:3001' 
          : 'https://setlist-playlist-generator.onrender.com';
        
        logger.log(`🎵 Searching Spotify API for: "${query}"`);
        const response = await fetch(`${apiUrl}/api/artists/search?q=${encodeURIComponent(query)}`);
        
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        
        const data = await response.json();
        
        // Convert Spotify API response to our Artist format
        // Backend returns { items: [...], source: 'unified', counts: {...}, total: n }
        const artistsArray = data.items || data.artists || [];
        // Filter to only show artists (not festivals) for now
        const artistsOnly = artistsArray.filter((item: any) => item.type === 'artist' || !item.type);
        const artists: Artist[] = artistsOnly.map((artist: any) => ({
          mbid: artist.mbid || artist.id || '', // Use id if mbid not available
          name: artist.name,
          sortName: artist.sortName || artist.name,
          disambiguation: artist.disambiguation || '',
          country: artist.country || '',
          type: artist.type || 'Person',
          verified: artist.verified || true,
          score: artist.score || 50,
          spotifyId: artist.spotifyId || artist.id, // Ensure spotifyId is set
          followers: artist.followers,
          genres: artist.genres,
          images: artist.images
        }));
        
        logger.log(`✅ Found ${artists.length} artists from Spotify API`);

        // Track artist search
        analytics.trackSearch({
          query,
          artist: artists.length > 0 ? artists[0].name : undefined,
          resultsCount: artists.length,
          searchType: 'manual'
        });

        setSuggestions(artists);
        if (artists.length > 0) {
          setDropdownPosition(calculateDropdownPosition());
          setShowDropdown(true);
        } else {
          setShowDropdown(false);
        }
        setHighlightedIndex(-1);
      } catch (error) {
        logger.error('❌ Spotify API search error:', error);
        // Track failed search
        analytics.trackSearch({
          query,
          resultsCount: 0,
          searchType: 'manual'
        });
        setSuggestions([]);
        setShowDropdown(false);
      } finally {
        setIsLoading(false);
      }
    }, 300)
  ).current;

  useEffect(() => {
    if (value.length >= 2) {
      debouncedSearch(value);
    } else if (value.length === 1) {
      // Show loading for single character but don't search yet
      setIsLoading(false);
      setSuggestions([]);
      setShowDropdown(false);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }
  }, [value, debouncedSearch]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSelectArtist(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setHighlightedIndex(-1);
        inputRef.current?.blur();
        break;
    }
  };

  const handleSelectArtist = (artist: Artist) => {
    onChange(artist.name);
    onSelect(artist);
    setShowDropdown(false);
    setHighlightedIndex(-1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  const handleInputFocus = async () => {
    // Only show dropdown if there are suggestions from typing
    if (value.length > 0 && suggestions.length > 0) {
      setDropdownPosition(calculateDropdownPosition());
      setShowDropdown(true);
    }
  };

  const handleInputBlur = (e: React.FocusEvent) => {
    setTimeout(() => {
      if (!dropdownRef.current?.contains(e.relatedTarget as Node)) {
        setShowDropdown(false);
        setHighlightedIndex(-1);
      }
    }, 200);
  };

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        onBlur={handleInputBlur}
        placeholder={placeholder}
        className="w-full p-3 border border-gray-300 rounded-lg outline-none transition-all duration-150 ease-in-out focus:border-teal-600 focus:shadow-sm"
        autoComplete="off"
      />
      
      {isLoading && (
        <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-teal-600"></div>
        </div>
      )}

      {showDropdown && suggestions.length > 0 && (
        <div
          ref={dropdownRef}
          className={`absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto ${
            dropdownPosition === 'above' 
              ? 'bottom-full mb-1' 
              : 'top-full mt-1'
          }`}
        >
          
          {/* Artist results */}
          {suggestions.map((artist, index) => (
            <div
              key={artist.mbid}
              className={`px-4 py-3 cursor-pointer transition-colors ${
                index === highlightedIndex 
                  ? 'bg-teal-50 text-teal-800' 
                  : 'hover:bg-gray-50'
              } ${
                index === suggestions.length - 1 ? 'rounded-b-lg' : 'border-b border-gray-100'
              }`}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelectArtist(artist)}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <div className="flex items-center gap-3">
                <div className="flex-1">
                  <div className="font-medium">
                    {artist.name}
                  </div>
                  {/* Show genres if available (from Spotify) */}
                  {artist.genres && artist.genres.length > 0 && (
                    <div className="text-sm text-gray-600 mt-0.5">
                      {artist.genres.slice(0, 3).join(', ')}
                    </div>
                  )}
                  {/* Show followers if available (from Spotify) */}
                  {artist.followers && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {artist.followers.toLocaleString()} followers
                    </div>
                  )}
                  {/* Fallback to disambiguation and type if no Spotify data */}
                  {!artist.genres && !artist.followers && (
                    <>
                      {artist.disambiguation && (
                        <div className="text-sm text-gray-600 mt-0.5">
                          {artist.disambiguation}
                        </div>
                      )}
                      {(artist.country || artist.type) && (
                        <div className="text-xs text-gray-500 mt-0.5">
                          {[artist.country, artist.type].filter(Boolean).join(' • ')}
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDropdown && suggestions.length === 0 && !isLoading && value.length >= 2 && (
        <div className={`absolute z-50 w-full bg-white border border-gray-200 rounded-lg shadow-lg ${
          dropdownPosition === 'above' 
            ? 'bottom-full mb-1' 
            : 'top-full mt-1'
        }`}>
          <div className="px-4 py-3 text-gray-500">
            No artists found
          </div>
        </div>
      )}
    </div>
  );
};

export default MockArtistAutocomplete;